import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowUp, ArrowDown, Trash2, Plus, Copy, Link2, Check, Download, Eye, Paperclip, ChevronDown, ChevronRight, ExternalLink, ImagePlus, X, ArrowRight, Info, Pencil } from 'lucide-react';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { puoFare } from '../../utils/permessi';
import { TIPI, SENZA_RISPOSTA, CON_OPZIONI, CON_FOTO, ANAGRAFICA, MODI_FIRMA, ACCETTA_DEFAULT, etichettaTipo, nuovoCampo, nuovoId, testoValutazione, convertiTipo, campiPersona, NOMI_PERSONA } from '../../utils/formCampi';
import FormAspetto, { Anteprima } from './FormAspetto';
import { aspettoCompleto, riduciFoto } from '../../components/form/formAspetto';
import AnteprimaDocumento from '../../components/AnteprimaDocumento';
import { apriPdf, paginaPdf, chiudiPdf } from '../../utils/pdfPagine';
import InFinestra from '../../components/InFinestra';
import TestoImpaginato from '../../components/form/TestoImpaginato';
import RisposteTabella, { testoRisposta } from './RisposteTabella';
import { Strumenti, RiepilogoPagamenti, FinestraPagamento, Statistiche, BadgePagamento } from './FormStrumenti';
import { s, STATO_COLORI, linkPubblico } from './FormLista';

// Editor di un form: domande, impostazioni, risposte, anteprima.
// confronto delle modifiche che non dipende dall'ordine delle chiavi
const stabile = (o) => JSON.stringify(o, (k, v) => (v && typeof v === 'object' && !Array.isArray(v) ? Object.fromEntries(Object.keys(v).sort().map(x => [x, v[x]])) : v));
// Link personalizzato: come lo normalizza il server (form.service.ts → normSlug), ma lascia il trattino finale mentre si scrive
const comeSlug = (v) => String(v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/['’]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+/, '').slice(0, 60);
const aLocale = (iso) => { if (!iso) return ''; const d = new Date(iso); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };

export default function FormEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const puoModificare = puoFare(user, 'form', 'edit');
  const puoEliminare = puoFare(user, 'form', 'delete');
  const puoCreare = puoFare(user, 'form', 'create');
  const [f, setF] = useState(null);
  const [orig, setOrig] = useState('');
  const [tab, setTab] = useState('domande');
  const [errore, setErrore] = useState('');
  const [salvato, setSalvato] = useState(false);
  const [copiato, setCopiato] = useState(false);
  const [auto, setAuto] = useState(null);     // esito dell'ultimo salvataggio automatico (solo bozze)
  const [backup, setBackup] = useState(null); // modifiche non salvate trovate su questo computer all'apertura
  const chiaveBackup = `form_modifiche_${id}`;

  const carica = (primo = false) => api.get(`/form/${id}`).then(r => {
    const d = { ...r.data, apertura_da: aLocale(r.data.apertura_da), chiusura_al: aLocale(r.data.chiusura_al), campi: r.data.campi || [], aspetto: aspettoCompleto(r.data.aspetto) };
    setF(d); setOrig(stabile(d));
    if (primo) {
      try {
        const b = JSON.parse(localStorage.getItem(chiaveBackup) || 'null');
        if (b?.dati && stabile({ ...b.dati, n_risposte: d.n_risposte, updated_at: d.updated_at }) !== stabile(d)) setBackup(b);
        else localStorage.removeItem(chiaveBackup);
      } catch { /* niente copia */ }
    }
  }).catch(e => setErrore(e.response?.data?.error || 'Form non trovato'));
  useEffect(() => { setBackup(null); setAuto(null); carica(true); }, [id]); // eslint-disable-line

  const modificato = f && stabile(f) !== orig;
  // il link che funziona davvero è quello salvato, non quello che si sta scrivendo
  const slugSalvato = useMemo(() => { try { return JSON.parse(orig).slug; } catch { return f?.slug; } }, [orig]); // eslint-disable-line

  // Copia di sicurezza delle modifiche non salvate su questo computer: se si esce per sbaglio, alla
  // riapertura si possono ripristinare. Finché c'è da decidere su una copia vecchia non la si sovrascrive.
  useEffect(() => {
    if (!f || backup) return undefined;
    const t = setTimeout(() => {
      try { if (modificato) localStorage.setItem(chiaveBackup, JSON.stringify({ quando: Date.now(), dati: f })); else localStorage.removeItem(chiaveBackup); } catch { /* spazio pieno o bloccato */ }
    }, 600);
    return () => clearTimeout(t);
  }, [f, modificato, backup, chiaveBackup]);
  // Salvataggio automatico dopo 2,5 s di pausa, anche sui form pubblicati (richiesta titolare 17/10: lavoro perso
  // su un form APERTO). Il link personalizzato no: cambia solo col SALVA, per non spostarlo mentre si scrive.
  const soloLink = f && modificato && stabile({ ...f, slug: slugSalvato }) === orig;
  useEffect(() => {
    if (!f || !modificato || soloLink || !puoModificare || backup) return undefined;
    const t = setTimeout(async () => {
      const foto = f; // senza stato: un PUBBLICA premuto nel frattempo non va annullato
      try { await api.put(`/form/${id}`, corpo(foto, { stato: undefined, slug: slugSalvato })); setOrig(stabile({ ...foto, slug: slugSalvato })); setAuto({ ok: true, ora: new Date() }); }
      catch (e) { setAuto({ ok: false, msg: e.response?.data?.error || 'errore di rete' }); }
    }, 2500);
    return () => clearTimeout(t);
  }, [f, modificato, backup]); // eslint-disable-line
  // chiudere o ricaricare la scheda con modifiche non salvate: il browser chiede conferma
  useEffect(() => {
    if (!modificato) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [modificato]);
  const ripristina = () => {
    // le foto le gestisce il server: si tengono quelle attuali
    setF(p => ({ ...backup.dati, id: p.id, slug: p.slug, stato: p.stato, n_risposte: p.n_risposte, updated_at: p.updated_at,
      aspetto: { ...backup.dati.aspetto, copertina_img: p.aspetto.copertina_img, sfondo_img: p.aspetto.sfondo_img, ...(p.aspetto.copertina_img ? {} : backup.dati.aspetto?.copertina === 'foto' ? { copertina: 'bcheers' } : {}), ...(p.aspetto.sfondo_img ? {} : backup.dati.aspetto?.sfondo === 'foto' ? { sfondo: 'bcheers' } : {}) } }));
    setBackup(null);
  };
  const scartaBackup = () => { try { localStorage.removeItem(chiaveBackup); } catch { /* niente */ } setBackup(null); };
  const statoSalvataggio = !modificato
    ? (salvato ? 'Salvato' : auto?.ok ? `Salvato in automatico alle ${auto.ora.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}` : 'Tutto salvato')
    : soloLink
      ? 'Link nuovo non ancora attivo: premi SALVA per usarlo'
      : auto && !auto.ok ? `Salvataggio automatico in attesa: ${auto.msg} (intanto c'è la copia su questo computer)` : 'Salvataggio automatico…';
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const setAspetto = (patch) => setF(p => ({ ...p, aspetto: { ...p.aspetto, ...patch } }));

  // Foto di copertina/sfondo: lette dal server con il token (il form può essere in bozza) e mostrate come blob
  const [url, setUrl] = useState({});
  const imgCop = f?.aspetto?.copertina_img, imgSfo = f?.aspetto?.sfondo_img;
  useEffect(() => {
    let vivi = true; const creati = [];
    const leggi = async (tipo, nome) => {
      if (!nome) return null;
      try { const r = await api.get(`/form/${id}/immagine/${tipo}`, { responseType: 'blob' }); const u = URL.createObjectURL(r.data); creati.push(u); return u; } catch { return null; }
    };
    Promise.all([leggi('copertina', imgCop), leggi('sfondo', imgSfo)]).then(([copertina, sfondo]) => { if (vivi) setUrl({ copertina, sfondo }); });
    return () => { vivi = false; creati.forEach(u => URL.revokeObjectURL(u)); };
  }, [id, imgCop, imgSfo]);
  // il server ha già salvato la foto: si allinea anche lo stato "salvato", così resta da salvare solo la scelta
  const allineaFoto = (tipo, nome, usa) => {
    const patch = (asp) => {
      const n = { ...asp, [`${tipo}_img`]: nome };
      if (!nome && asp[tipo] === 'foto') n[tipo] = 'bcheers';
      return n;
    };
    setOrig(o => { const d = JSON.parse(o); d.aspetto = patch(d.aspetto); return stabile(d); });
    setF(p => ({ ...p, aspetto: { ...patch(p.aspetto), ...(usa ? { [tipo]: 'foto' } : {}) } }));
  };
  const caricaFoto = async (tipo, dati) => {
    const r = await api.post(`/form/${id}/immagine/${tipo}`, { dati });
    allineaFoto(tipo, r.data.nome, true);
  };
  const togliFoto = async (tipo) => {
    if (!window.confirm('Togliere la foto? Il form torna alla grafica del tema.')) return;
    try { await api.delete(`/form/${id}/immagine/${tipo}`); allineaFoto(tipo, null, false); }
    catch (e) { alert(e.response?.data?.error || 'Errore'); }
  };
  // Foto delle opzioni: lette con il token e tenute come blob finché l'editor è aperto
  const [fotoMap, setFotoMap] = useState({});
  const fotoChieste = useRef(new Set());
  const nomiFoto = (f?.campi || []).flatMap(c => [...(c.foto_opzioni || []), ...(c.pagine_doc || [])]).filter(Boolean).join('|');
  useEffect(() => {
    for (const nome of nomiFoto ? nomiFoto.split('|') : []) {
      if (fotoChieste.current.has(nome)) continue;
      fotoChieste.current.add(nome);
      api.get(`/form/${id}/foto-opzione/${nome}`, { responseType: 'blob' })
        .then(r => setFotoMap(m => ({ ...m, [nome]: URL.createObjectURL(r.data) })))
        .catch(() => { /* foto sparita: il riquadro resta senza */ });
    }
  }, [id, nomiFoto]);
  useEffect(() => () => Object.values(fotoMap).forEach(u => URL.revokeObjectURL(u)), []); // eslint-disable-line
  // tipo "pagina" = pagina immagine di un regolamento: più grande, deve restare leggibile
  const caricaFotoOpzione = async (file, tipo) => {
    const { dati } = await riduciFoto(file, tipo === 'pagina' ? 1800 : 900);
    const r = await api.post(`/form/${id}/foto-opzione`, { dati, tipo });
    fotoChieste.current.add(r.data.nome);
    setFotoMap(m => ({ ...m, [r.data.nome]: dati }));
    return r.data.nome;
  };
  const urlVista = useMemo(() => ({ ...url, foto: (nome) => fotoMap[nome] || null }), [url, fotoMap]);

  const setCampo = (i, patch, intero) => setF(p => ({ ...p, campi: p.campi.map((c, j) => j === i ? (intero || { ...c, ...patch }) : c) }));
  const sposta = (i, d) => setF(p => { const c = [...p.campi]; const j = i + d; if (j < 0 || j >= c.length) return p; [c[i], c[j]] = [c[j], c[i]]; return { ...p, campi: c }; });
  // nuova domanda nel punto scelto (pos = indice), poi la si porta in vista col cursore nel titolo
  const aggiungi = (tipo, pos) => {
    const c = nuovoCampo(tipo);
    setF(p => { const campi = [...p.campi]; campi.splice(pos ?? campi.length, 0, c); return { ...p, campi }; });
    setTimeout(() => {
      const el = document.querySelector(`[data-campo="${c.id}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.querySelector('input')?.focus({ preventScroll: true });
    }, 50);
  };

  function corpo(f, extra = {}) {
    return {
      titolo: f.titolo, descrizione: f.descrizione, campi: f.campi, stato: f.stato, genera_codice: f.genera_codice, prefisso_codice: f.prefisso_codice,
      collega_anagrafica: f.collega_anagrafica, messaggio_conferma: f.messaggio_conferma, email_oggetto: f.email_oggetto, email_testo: f.email_testo, email_conferma: f.email_conferma, smtp_config_id: f.smtp_config_id || null, max_risposte: f.max_risposte || null,
      apertura_da: f.apertura_da ? new Date(f.apertura_da).toISOString() : null, chiusura_al: f.chiusura_al ? new Date(f.chiusura_al).toISOString() : null, aspetto: f.aspetto, slug: f.slug, ...extra,
    };
  }
  // SALVA sempre attivo anche col salvataggio automatico: se è già tutto salvato conferma soltanto
  const salva = async (extra = {}) => {
    setErrore('');
    if (!modificato && !Object.keys(extra).length) { setSalvato(true); setTimeout(() => setSalvato(false), 1500); return; }
    try {
      await api.put(`/form/${id}`, corpo(f, extra));
      try { localStorage.removeItem(chiaveBackup); } catch { /* niente */ }
      await carica();
      setSalvato(true); setTimeout(() => setSalvato(false), 1500);
    } catch (e) { setErrore(e.response?.data?.error || 'Errore nel salvataggio'); window.scrollTo({ top: 0, behavior: 'smooth' }); }
  };
  const copiaLink = async () => {
    try { await navigator.clipboard.writeText(linkPubblico(slugSalvato)); setCopiato(true); setTimeout(() => setCopiato(false), 1500); }
    catch { window.prompt('Copia il link:', linkPubblico(slugSalvato)); }
  };
  const elimina = async () => {
    if (!window.confirm(`Eliminare "${f.titolo}"${f.n_risposte ? ` e le sue ${f.n_risposte} risposte con gli allegati` : ''}? Non si può annullare.`)) return;
    try { await api.delete(`/form/${id}`); navigate('/form'); } catch (e) { alert(e.response?.data?.error || 'Errore'); }
  };
  const duplica = async () => {
    try { const r = await api.post(`/form/${id}/duplica`); navigate(`/form/${r.data.id}`); } catch (e) { alert(e.response?.data?.error || 'Errore'); }
  };

  if (!f) return <div style={{ color: 'var(--text-dim)' }}>{errore || 'Caricamento…'}</div>;

  return (
    <div>
      <button style={{ ...s.btnSec, marginBottom: '14px' }} onClick={() => { if (modificato && !window.confirm(soloLink ? 'Il link nuovo non è ancora salvato: resta quello di prima finché non premi SALVA. Uscire lo stesso?' : 'Il salvataggio automatico non ha ancora salvato le ultime modifiche (ne resta una copia su questo computer). Uscire lo stesso?')) return; navigate('/form'); }}><ArrowLeft size={13} /> TUTTI I FORM</button>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap', marginBottom: '10px' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ ...s.title, overflowWrap: 'anywhere' }}>{f.titolo || 'Senza titolo'}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
            <span style={{ ...s.badge, color: STATO_COLORI[f.stato], borderColor: STATO_COLORI[f.stato] + '60' }}>{f.stato}</span>
            <span style={s.meta}>{f.n_risposte} risposte</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {f.stato !== 'BOZZA' && <>
            <button style={s.btnSec} onClick={copiaLink}>{copiato ? <><Check size={13} /> COPIATO</> : <><Link2 size={13} /> COPIA LINK</>}</button>
            <a style={{ ...s.btnSec, textDecoration: 'none' }} href={`/f/${slugSalvato}`} target="_blank" rel="noreferrer"><ExternalLink size={13} /> APRI</a>
          </>}
          {puoModificare && f.stato === 'BOZZA' && <button style={s.btnPri} onClick={() => salva({ stato: 'APERTO' })}>PUBBLICA</button>}
          {puoModificare && f.stato === 'APERTO' && <button style={s.btnSec} onClick={() => salva({ stato: 'CHIUSO' })}>CHIUDI</button>}
          {puoModificare && f.stato === 'CHIUSO' && <button style={s.btnSec} onClick={() => salva({ stato: 'APERTO' })}>RIAPRI</button>}
          {puoCreare && <button style={s.btnSec} onClick={duplica} title="Duplica"><Copy size={13} /></button>}
          {puoEliminare && <button style={s.btnSec} onClick={elimina} title="Elimina"><Trash2 size={13} /></button>}
        </div>
      </div>
      {f.stato !== 'BOZZA' && <div style={{ ...s.meta, marginBottom: '10px', overflowWrap: 'anywhere' }}>Link pubblico: {linkPubblico(slugSalvato)}</div>}

      <div style={{ display: 'flex', gap: '20px', borderBottom: '1px solid var(--border)', marginBottom: '18px', overflowX: 'auto' }}>
        {[['domande', 'DOMANDE'], ['aspetto', 'ASPETTO'], ['impostazioni', 'IMPOSTAZIONI'], ['strumenti', 'STRUMENTI'], ['risposte', `RISPOSTE (${f.n_risposte})`], ['anteprima', 'ANTEPRIMA']].map(([k, l]) => (
          <button key={k} style={{ ...s.tab, color: tab === k ? '#F4600C' : undefined, borderBottomColor: tab === k ? '#F4600C' : 'transparent' }} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>

      {backup && (
        <div style={{ ...s.card, borderLeft: '3px solid #F59E0B', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: '220px', fontSize: 'var(--font-size-sm)' }}>
            Su questo computer ci sono <b>modifiche non salvate</b> di questo form ({new Date(backup.quando).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}, {(backup.dati.campi || []).length} domande). Vuoi riprenderle?
          </span>
          {puoModificare && <button style={s.btnPri} onClick={ripristina}>RIPRISTINA</button>}
          <button style={s.btnSec} onClick={scartaBackup}>SCARTA</button>
        </div>
      )}
      {errore && <div style={s.errore}>{errore}</div>}

      {(tab === 'domande' || tab === 'impostazioni') && (
        <fieldset disabled={!puoModificare} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
          {tab === 'domande' && <Domande f={f} set={set} setCampo={setCampo} sposta={sposta} aggiungi={aggiungi} fotoMap={fotoMap} caricaFotoOpzione={caricaFotoOpzione} />}
          {tab === 'impostazioni' && <Impostazioni f={f} set={set} slugSalvato={slugSalvato} />}
          {puoModificare && (
            <div style={barraSalva}>
              <span style={{ ...s.meta, color: modificato && auto && !auto.ok ? '#F59E0B' : undefined }}>{statoSalvataggio}</span>
              <button type="button" style={s.btnPri} onClick={() => salva()}><Check size={14} /> SALVA</button>
            </div>
          )}
        </fieldset>
      )}
      {tab === 'aspetto' && <>
         <FormAspetto f={f} setAspetto={setAspetto} url={urlVista} caricaFoto={caricaFoto} togliFoto={togliFoto} puoModificare={puoModificare} />
        {puoModificare && (
          <div style={barraSalva}>
            <span style={{ ...s.meta, color: modificato && auto && !auto.ok ? '#F59E0B' : undefined }}>{statoSalvataggio}</span>
            <button type="button" style={s.btnPri} onClick={() => salva()}><Check size={14} /> SALVA</button>
          </div>
        )}
      </>}
      {tab === 'risposte' && <Risposte f={f} puoEliminare={puoEliminare} puoModificare={puoFare(user, 'form', 'edit')} puoAggiungere={puoFare(user, 'form', 'create')} onCambio={carica} />}
      {tab === 'strumenti' && <Strumenti key={f.updated_at} f={f} puoModificare={puoModificare} onSalvato={carica} />}
      {tab === 'anteprima' && <Anteprima f={f} url={urlVista} />}
    </div>
  );
}

function Domande({ f, set, setCampo, sposta, aggiungi, fotoMap, caricaFotoOpzione }) {
  const [inserisciA, setInserisciA] = useState(null); // posizione in cui si sta scegliendo il tipo della nuova domanda
  const cp = campiPersona(f.campi);
  const ruoloDi = (id) => Object.keys(cp).find(k => cp[k].id === id);
  const cambiaTipo = (i, c, tipo) => {
    if (tipo === c.tipo) return;
    const perde = [];
    if (CON_OPZIONI.includes(c.tipo) && !CON_OPZIONI.includes(tipo) && c.opzioni?.length) perde.push('le opzioni');
    if (c.foto_opzioni?.some(Boolean) && !CON_FOTO.includes(tipo)) perde.push('le foto delle opzioni');
    if (c.anagrafica && !ANAGRAFICA.some(a => a.chiave === c.anagrafica && a.tipo === tipo)) perde.push(`il collegamento alla scheda (${c.anagrafica})`);
    if (c.tipo === 'DOCUMENTO' && (c.testo_doc || c.pagine_doc?.length)) perde.push('il testo e le pagine del regolamento');
    if (perde.length && !window.confirm(`Diventando "${etichettaTipo(tipo)}" la domanda perde ${perde.join(', ')}. Continuare?`)) return;
    setCampo(i, null, convertiTipo(c, tipo));
  };
  const [caricando, setCaricando] = useState(null); // "<campo>_<opzione>" mentre la foto sale
  const scegliFoto = (i, c, k) => {
    const inp = document.createElement('input');
    inp.type = 'file'; inp.accept = 'image/jpeg,image/png,image/webp';
    inp.onchange = async () => {
      const file = inp.files?.[0];
      if (!file) return;
      setCaricando(`${c.id}_${k}`);
      try {
        const nome = await caricaFotoOpzione(file);
        setCampo(i, { foto_opzioni: c.opzioni.map((_, j) => j === k ? nome : ((c.foto_opzioni || [])[j] || null)) });
      } catch (e) { alert(e.response?.data?.error || e.message || 'Foto non caricata'); }
      finally { setCaricando(null); }
    };
    inp.click();
  };
  return <>
    <div style={s.card}>
      <label style={s.label}>TITOLO</label>
      <input style={{ ...s.inp, fontSize: 'var(--font-size-lg)', fontWeight: 700 }} value={f.titolo} maxLength={200} onChange={e => set('titolo', e.target.value)} />
      <label style={{ ...s.label, marginTop: '12px' }}>DESCRIZIONE (mostrata in cima al modulo)</label>
      <textarea style={{ ...s.inp, minHeight: '70px', resize: 'vertical' }} value={f.descrizione || ''} maxLength={5000} onChange={e => set('descrizione', e.target.value)} />
    </div>

    {f.campi.map((c, i) => (
      <React.Fragment key={c.id}>
      <Inserisci aperto={inserisciA === i} onApri={() => setInserisciA(inserisciA === i ? null : i)} onScegli={tipo => { aggiungi(tipo, i); setInserisciA(null); }} />
      <div data-campo={c.id} style={{ ...s.card, marginBottom: 0, borderLeft: `3px solid ${c.tipo === 'SEZIONE' ? '#F4600C' : c.tipo === 'INFO' ? '#3B82F6' : 'var(--border2)'}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
          <span style={{ ...s.badge, color: '#F4600C', borderColor: '#F4600C50' }}>{i + 1}</span>
          <select style={{ ...s.inp, width: 'auto', padding: '4px 8px', fontSize: 'var(--font-size-xs)', fontWeight: 700 }} value={c.tipo} onChange={e => cambiaTipo(i, c, e.target.value)} title="Cambia il tipo di domanda">
            {TIPI.map(t => <option key={t.tipo} value={t.tipo}>{t.label}</option>)}
          </select>
          {ruoloDi(c.id) && <span style={{ ...s.badge, color: '#3B82F6', borderColor: '#3B82F650' }} title={cp[ruoloDi(c.id)].auto ? 'Riconosciuta dal titolo della domanda' : 'Collegata a mano'}>
            {(NOMI_PERSONA[ruoloDi(c.id)] || ruoloDi(c.id)).toUpperCase()}{cp[ruoloDi(c.id)].auto ? ' · RICONOSCIUTA' : ''}</span>}
          {!ruoloDi(c.id) && c.anagrafica && <span style={{ ...s.badge, color: '#3B82F6', borderColor: '#3B82F650' }}>→ SCHEDA: {c.anagrafica.toUpperCase()}</span>}
          <div style={{ flex: 1 }} />
          <button type="button" style={s.iconBtn} onClick={() => sposta(i, -1)} disabled={i === 0} title="Sposta su"><ArrowUp size={15} /></button>
          <button type="button" style={s.iconBtn} onClick={() => sposta(i, 1)} disabled={i === f.campi.length - 1} title="Sposta giù"><ArrowDown size={15} /></button>
          <button type="button" style={s.iconBtn} onClick={() => set('campi', [...f.campi.slice(0, i + 1), { ...c, id: nuovoId(), anagrafica: null }, ...f.campi.slice(i + 1)])} title="Duplica domanda"><Copy size={14} /></button>
          <button type="button" style={{ ...s.iconBtn, color: '#EF4444' }} onClick={() => set('campi', f.campi.filter((_, j) => j !== i))} title="Elimina domanda"><Trash2 size={14} /></button>
        </div>
        <input style={{ ...s.inp, fontWeight: 600 }} placeholder={c.tipo === 'SEZIONE' ? 'Titolo della sezione' : c.tipo === 'INFO' ? 'Titolo (es. Come funziona)' : 'Domanda'} value={c.etichetta} maxLength={300} onChange={e => setCampo(i, { etichetta: e.target.value })} />
        <input style={{ ...s.inp, marginTop: '8px', fontSize: 'var(--font-size-xs)' }} placeholder="Testo di aiuto (facoltativo)" value={c.descrizione || ''} maxLength={2000} onChange={e => setCampo(i, { descrizione: e.target.value })} />

        {CON_OPZIONI.includes(c.tipo) && (
          <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {(c.opzioni || []).map((o, k) => {
              const conNote = c.tipo !== 'ELENCO';
              const note = c.note_opzioni || [];
              const foto = (c.foto_opzioni || [])[k];
              return (
                <div key={k} style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                  {CON_FOTO.includes(c.tipo) && (foto
                    ? <span style={{ position: 'relative', flexShrink: 0 }}>
                        <img src={fotoMap[foto] || ''} alt="" style={{ width: 52, height: 38, objectFit: 'cover', borderRadius: 6, display: 'block', background: 'var(--bg-input)', cursor: 'pointer' }} title="Cambia foto" onClick={() => scegliFoto(i, c, k)} />
                        <button type="button" title="Togli foto" onClick={() => setCampo(i, { foto_opzioni: c.opzioni.map((_, j) => j === k ? null : ((c.foto_opzioni || [])[j] || null)) })}
                          style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', border: 0, background: '#1D1D1F', color: '#fff', display: 'grid', placeItems: 'center', padding: 0, cursor: 'pointer' }}><X size={11} /></button>
                      </span>
                    : <button type="button" style={{ ...s.iconBtn, width: 52, height: 38, border: '1px dashed var(--border2)', borderRadius: 6 }} title="Aggiungi una foto all'opzione" onClick={() => scegliFoto(i, c, k)} disabled={caricando === `${c.id}_${k}`}>
                        {caricando === `${c.id}_${k}` ? '…' : <ImagePlus size={15} />}
                      </button>)}
                  <input style={{ ...s.inp, flex: '1 1 200px', width: 'auto' }} value={o} maxLength={200} onChange={e => setCampo(i, { opzioni: c.opzioni.map((x, j) => j === k ? e.target.value : x) })} />
                  {conNote && <input style={{ ...s.inp, flex: '1 1 200px', width: 'auto', fontSize: 'var(--font-size-xs)' }} placeholder="Sottotitolo (facoltativo, es. orari)" value={note[k] || ''} maxLength={120}
                    onChange={e => setCampo(i, { note_opzioni: c.opzioni.map((_, j) => j === k ? e.target.value : (note[j] || '')) })} />}
                  <button type="button" style={s.iconBtn} onClick={() => setCampo(i, { opzioni: c.opzioni.filter((_, j) => j !== k), note_opzioni: note.filter((_, j) => j !== k), ...(c.foto_opzioni ? { foto_opzioni: c.foto_opzioni.filter((_, j) => j !== k) } : {}) })} title="Togli opzione"><Trash2 size={13} /></button>
                </div>
              );
            })}
            <button type="button" style={{ ...s.btnSec, alignSelf: 'flex-start' }} onClick={() => setCampo(i, { opzioni: [...(c.opzioni || []), `Opzione ${(c.opzioni || []).length + 1}`], ...(c.note_opzioni ? { note_opzioni: [...c.note_opzioni, ''] } : {}), ...(c.foto_opzioni ? { foto_opzioni: [...c.foto_opzioni, null] } : {}) })}><Plus size={12} /> OPZIONE</button>
            {c.tipo !== 'ELENCO' && <div style={s.help}>Nel form le scelte compaiono come riquadri grandi; il sottotitolo va sotto il nome dell'opzione. Con <ImagePlus size={11} /> metti una foto sopra all'opzione (sede, divisa, camp…): meglio se tutte le opzioni ce l'hanno.</div>}
          </div>
        )}
        {c.tipo === 'ALLEGATO' && (
          <div style={{ display: 'flex', gap: '12px', marginTop: '10px', flexWrap: 'wrap' }}>
            <div><label style={s.label}>NUMERO MASSIMO DI FILE</label>
              <select style={{ ...s.inp, width: '120px' }} value={c.max_file || 1} onChange={e => setCampo(i, { max_file: +e.target.value })}>{[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}</select></div>
            <div><label style={s.label}>MB MASSIMI PER FILE</label>
              <select style={{ ...s.inp, width: '120px' }} value={c.max_mb || 5} onChange={e => setCampo(i, { max_mb: +e.target.value })}>{[1, 2, 5, 10].map(n => <option key={n} value={n}>{n} MB</option>)}</select></div>
            <div style={{ ...s.help, alignSelf: 'flex-end' }}>Formati accettati: PDF, JPG, PNG.</div>
          </div>
        )}
        {c.tipo === 'VALUTAZIONE' && (
          <div style={{ display: 'flex', gap: '12px', marginTop: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div><label style={s.label}>STILE</label>
              <select style={{ ...s.inp, width: '160px' }} value={c.stile || 'stelle'} onChange={e => setCampo(i, { stile: e.target.value })}>
                <option value="stelle">Stelle</option><option value="faccine">Faccine</option>
              </select></div>
            <div style={s.help}>Da 1 a 5: {testoValutazione(4, c.stile)}. Nell'Excel trovi il numero.</div>
          </div>
        )}
        {c.tipo === 'CODICE_FISCALE' && cp.codice_fiscale?.id === c.id && (
          <div style={{ ...s.help, marginTop: '8px' }}>
            Il codice fiscale viene confrontato con: {['nome', 'cognome', 'data_nascita', 'luogo_nascita'].map(k => (
              <span key={k} style={{ marginRight: '10px', color: cp[k] ? '#22C55E' : '#F59E0B', fontWeight: 600 }}>{cp[k] ? '✓' : '✗'} {NOMI_PERSONA[k]}</span>
            ))}
            <span style={{ display: 'block', marginTop: '3px' }}>Il sesso non serve chiederlo: lo dice il codice fiscale (lo trovi nell'Excel). {['nome', 'cognome', 'data_nascita', 'luogo_nascita'].some(k => !cp[k]) && 'Per i ✗ aggiungi la domanda (meglio prima del codice fiscale) o scegli "Dato della persona" su quella che c\'è già.'}</span>
          </div>
        )}
        {c.tipo === 'CODICE_FISCALE' && cp.codice_fiscale?.id === c.id && (
          <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginTop: '8px', fontSize: 'var(--font-size-sm)', cursor: 'pointer' }}>
            <input type="checkbox" checked={c.stranieri !== false} onChange={e => setCampo(i, { stranieri: e.target.checked ? undefined : false })} style={{ marginTop: '3px' }} />
            <span><b>Accetta stranieri senza codice fiscale italiano</b>
              <span style={{ ...s.help, display: 'block' }}>Compare la casella "Sono straniero/a": chi la spunta sceglie lo stato di nascita, il sesso e carica il documento (passaporto o carta d'identità). Il codice fiscale lo calcola il gestionale; chi risulta nato in Italia non può usarla. La scheda in anagrafica resta "da verificare" finché non controllate il documento (Anagrafica → VERIFICA DOCUMENTO).</span></span>
          </label>
        )}
        {c.tipo === 'FIRMA' && <ImpostazioniFirma c={c} i={i} setCampo={setCampo} conNome={!!(cp.nome && cp.cognome)} />}
        {c.tipo === 'INFO' && <EditorDocumento info c={c} i={i} setCampo={setCampo} fotoMap={fotoMap} caricaPagina={file => caricaFotoOpzione(file, 'pagina')} />}
        {c.tipo === 'DOCUMENTO' && <EditorDocumento c={c} i={i} setCampo={setCampo} fotoMap={fotoMap} caricaPagina={file => caricaFotoOpzione(file, 'pagina')} />}
        {c.tipo === 'ALLEGATO' && <div style={{ ...s.help, marginTop: '4px' }}>Da telefono c'è anche il pulsante <b>Scatta foto</b>: la foto viene alleggerita da sola.</div>}
        {c.tipo === 'NUMERO' && (
          <div style={{ display: 'flex', gap: '12px', marginTop: '10px' }}>
            <div><label style={s.label}>MINIMO</label><input type="number" style={{ ...s.inp, width: '120px' }} value={c.min ?? ''} onChange={e => setCampo(i, { min: e.target.value === '' ? null : +e.target.value })} /></div>
            <div><label style={s.label}>MASSIMO</label><input type="number" style={{ ...s.inp, width: '120px' }} value={c.max ?? ''} onChange={e => setCampo(i, { max: e.target.value === '' ? null : +e.target.value })} /></div>
          </div>
        )}
        {!SENZA_RISPOSTA.includes(c.tipo) && (
          <div style={{ display: 'flex', gap: '18px', marginTop: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: 'var(--font-size-sm)', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!c.obbligatorio} onChange={e => setCampo(i, { obbligatorio: e.target.checked })} style={{ accentColor: '#F4600C' }} /> Obbligatoria
            </label>
            {ANAGRAFICA.some(a => a.tipo === c.tipo) && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: 'var(--font-size-sm)' }}>
                Dato della persona:
                <select style={{ ...s.inp, width: 'auto', padding: '5px 8px' }} value={c.anagrafica || ''} onChange={e => setCampo(i, { anagrafica: e.target.value || null })}>
                  <option value="">{ruoloDi(c.id) && cp[ruoloDi(c.id)].auto ? `Automatico (${NOMI_PERSONA[ruoloDi(c.id)]})` : '—'}</option>
                  {ANAGRAFICA.filter(a => a.tipo === c.tipo).map(a => <option key={a.chiave} value={a.chiave}>{a.label}</option>)}
                </select>
                <NotaDatoPersona auto={ruoloDi(c.id) && cp[ruoloDi(c.id)].auto ? NOMI_PERSONA[ruoloDi(c.id)] : null} />
              </label>
            )}
          </div>
        )}
      </div>
      </React.Fragment>
    ))}

    <div style={{ ...s.card, marginTop: '14px' }}>
      <div style={{ ...s.cardTitle, marginBottom: '10px' }}>AGGIUNGI IN FONDO</div>
      <SceltaTipo onScegli={tipo => aggiungi(tipo)} />
      <div style={{ ...s.help, marginTop: '8px' }}>Per metterla in un altro punto usa <b>+ DOMANDA QUI</b> tra una domanda e l'altra. Il tipo si cambia anche dopo, dal menu in cima a ogni domanda.</div>
    </div>
    <div style={{ ...s.card, borderLeft: '3px solid #10B981' }}>
      <div style={{ ...s.cardTitle, marginBottom: '10px' }}>DOPO L'INVIO VEDRANNO</div>
      <textarea style={{ ...s.inp, minHeight: '60px', resize: 'vertical' }} maxLength={2000} value={f.messaggio_conferma || ''} onChange={e => set('messaggio_conferma', e.target.value)} placeholder="Grazie, abbiamo ricevuto le tue risposte." />
      <div style={{ ...s.help, marginTop: '6px' }}>Messaggio della pagina finale, sotto "Grazie Nome!". Vuoto = "Grazie, abbiamo ricevuto le tue risposte." L'email a chi compila si imposta in <b>Impostazioni</b>.</div>
    </div>
  </>;
}

// Tra una domanda e l'altra: "+ DOMANDA QUI", che si apre sui tipi disponibili
function Inserisci({ aperto, onApri, onScegli }) {
  const [sopra, setSopra] = useState(false);
  return (
    <div style={{ padding: aperto ? '8px 0' : 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '30px', opacity: aperto || sopra ? 1 : 0.55 }} onMouseEnter={() => setSopra(true)} onMouseLeave={() => setSopra(false)}>
        <div style={{ flex: 1, borderTop: `1px dashed ${aperto || sopra ? '#F4600C' : 'var(--border2)'}` }} />
        <button type="button" onClick={onApri} style={{ display: 'flex', alignItems: 'center', gap: '4px', background: aperto ? '#F4600C' : 'var(--bg-input)', color: aperto ? '#fff' : '#F4600C', border: '1px solid #F4600C60', borderRadius: '999px', padding: '3px 11px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
          <Plus size={12} /> {aperto ? 'CHIUDI' : 'DOMANDA QUI'}
        </button>
        <div style={{ flex: 1, borderTop: `1px dashed ${aperto || sopra ? '#F4600C' : 'var(--border2)'}` }} />
      </div>
      {aperto && <div style={{ ...s.card, marginTop: '6px', marginBottom: 0 }}><SceltaTipo onScegli={onScegli} /></div>}
    </div>
  );
}

function SceltaTipo({ onScegli }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '6px' }}>
      {TIPI.map(t => (
        <button key={t.tipo} type="button" onClick={() => onScegli(t.tipo)}
          style={{ ...s.btnSec, justifyContent: 'flex-start', whiteSpace: 'normal', textAlign: 'left', color: t.tipo === 'SEZIONE' ? '#F4600C' : t.tipo === 'INFO' ? '#3B82F6' : 'var(--text)' }}>
          <Plus size={12} style={{ flexShrink: 0 }} /> {t.label}
        </button>
      ))}
    </div>
  );
}

// "A cosa serve": spiegazione in parole semplici in testa a ogni impostazione (richiesta titolare 17/10)
const Spiega = ({ children }) => <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-dim)', background: 'var(--bg-input)', borderRadius: '10px', padding: '9px 12px', margin: '-4px 0 12px', lineHeight: 1.5 }}><b style={{ color: 'var(--text)' }}>A cosa serve:</b> {children}</div>;

function Impostazioni({ f, set, slugSalvato }) {
  const riga = { display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: 'var(--font-size-sm)', cursor: 'pointer' };
  return <>
    <LinkForm f={f} set={set} slugSalvato={slugSalvato} />
    <div style={s.card}>
      <div style={s.cardTitle}>CODICE IDENTIFICATIVO</div>
      <Spiega>dà a ogni persona che compila un codice tutto suo (es. OD-7K4MPX), che vede alla fine e riceve nell'email. Utile quando dovete ritrovare velocemente una risposta: all'open day la persona dice il codice e la trovate subito nelle Risposte. Se non vi serve, lasciatelo spento.</Spiega>
      <label style={riga}><input type="checkbox" checked={!!f.genera_codice} onChange={e => set('genera_codice', e.target.checked)} style={{ accentColor: '#F4600C', marginTop: '3px' }} />
        <span>Genera un codice per ogni risposta<span style={{ display: 'block', ...s.help }}>Il prefisso (es. OD) fa capire a colpo d'occhio da quale form arriva.</span></span></label>
      {f.genera_codice && <div style={{ marginTop: '10px' }}><label style={s.label}>PREFISSO (facoltativo, max 6)</label>
        <input style={{ ...s.inp, width: '140px', textTransform: 'uppercase' }} maxLength={6} value={f.prefisso_codice || ''} onChange={e => set('prefisso_codice', e.target.value)} placeholder="OD" /></div>}
    </div>
    <div style={s.card}>
      <div style={s.cardTitle}>ANAGRAFICA PERSONE</div>
      <Spiega>collega chi compila alla sua scheda in Anagrafica persone, riconoscendola dal codice fiscale. Se la persona è nuova le crea la scheda (niente da ribattere a mano quando diventa corsista o si tessera); se c'è già aggancia la risposta a quella, senza doppioni. Tenetelo acceso per iscrizioni e adesioni di atleti; spento per sondaggi o form anonimi.</Spiega>
      <label style={riga}><input type="checkbox" checked={!!f.collega_anagrafica} onChange={e => set('collega_anagrafica', e.target.checked)} style={{ accentColor: '#F4600C', marginTop: '3px' }} />
        <span>Crea o riconosce la scheda in anagrafica dal codice fiscale<span style={{ display: 'block', ...s.help }}>Servono tre domande obbligatorie: Nome, Cognome e Codice fiscale. Quelle con il badge blu RICONOSCIUTA vanno già bene; se una ha un titolo diverso, nella scheda Domande scegli "Dato della persona". Se il CF esiste già, la risposta si aggancia a quella scheda senza modificarla.</span></span></label>
    </div>
    <div style={s.card}>
      <div style={s.cardTitle}>APERTURA</div>
      <Spiega>decide quando il form accetta risposte. Potete prepararlo prima e farlo aprire da solo a una data, farlo chiudere da solo a una scadenza, oppure fissare un numero di posti: finiti i posti, chi apre il link trova il form chiuso.</Spiega>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
        <div><label style={s.label}>APRE IL (facoltativo)</label><input type="datetime-local" style={s.inp} value={f.apertura_da || ''} onChange={e => set('apertura_da', e.target.value)} /></div>
        <div><label style={s.label}>CHIUDE IL (facoltativo)</label><input type="datetime-local" style={s.inp} value={f.chiusura_al || ''} onChange={e => set('chiusura_al', e.target.value)} /></div>
        <div><label style={s.label}>MASSIMO RISPOSTE (posti)</label><input type="number" min={1} style={s.inp} value={f.max_risposte ?? ''} onChange={e => set('max_risposte', e.target.value === '' ? null : +e.target.value)} placeholder="Nessun limite" /></div>
      </div>
      <p style={s.help}>Il modulo accetta risposte solo se è PUBBLICATO e dentro queste date. Raggiunto il massimo si chiude da solo.</p>
    </div>
    <div style={s.card}>
      <div style={s.cardTitle}>MESSAGGIO DOPO L'INVIO</div>
      <Spiega>è quello che la persona legge sullo schermo appena ha inviato, per esempio "Grazie! Ti contatteremo entro venerdì".</Spiega>
      <textarea style={{ ...s.inp, minHeight: '60px', resize: 'vertical' }} maxLength={2000} value={f.messaggio_conferma || ''} onChange={e => set('messaggio_conferma', e.target.value)} placeholder="Grazie, abbiamo ricevuto le tue risposte." />
      <p style={s.help}>Compare a video dopo l'invio (si modifica anche in fondo alle Domande). È anche il testo dell'email qui sotto, se non le scrivi un testo suo.</p>
    </div>
    <EmailConferma f={f} set={set} />
  </>;
}

function EmailConferma({ f, set }) {
  const [caselle, setCaselle] = useState([]);
  useEffect(() => { api.get('/form/caselle-email').then(r => setCaselle(r.data)).catch(() => setCaselle([])); }, []);
  const campoEmail = f.campi.find(c => c.anagrafica === 'email') || f.campi.find(c => c.tipo === 'EMAIL');
  const riga = { display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: 'var(--font-size-sm)', cursor: 'pointer' };
  return (
    <div style={s.card}>
      <div style={s.cardTitle}>EMAIL DI CONFERMA</div>
      <Spiega>manda in automatico un'email a chi ha compilato, così ha la conferma per iscritto (e il codice, se acceso). Ogni form può avere il suo oggetto, il suo testo e la casella da cui parte.</Spiega>
      <label style={riga}><input type="checkbox" checked={!!f.email_conferma} onChange={e => set('email_conferma', e.target.checked)} style={{ accentColor: '#F4600C', marginTop: '3px' }} />
        <span>Manda a chi compila un'email di conferma{f.genera_codice ? ' con il suo codice' : ''}
          <span style={{ display: 'block', ...s.help }}>{campoEmail
            ? <>Destinatario: la risposta alla domanda "{campoEmail.etichetta}".</>
            : <span style={{ color: '#F59E0B' }}>Aggiungi una domanda di tipo Email, altrimenti non c'è un indirizzo a cui scrivere.</span>}</span></span></label>
      {f.email_conferma && <div style={{ marginTop: '12px' }}>
        <label style={s.label}>SCRIVE DA</label>
        <select style={{ ...s.inp, width: 'auto', minWidth: '260px' }} value={f.smtp_config_id || ''} onChange={e => set('smtp_config_id', e.target.value ? +e.target.value : null)}>
          <option value="">Casella scelta per i Form in Impostazioni → Email</option>
          {caselle.map(c => <option key={c.id} value={c.id}>{c.nome} — {c.smtp_from || c.smtp_user || 'non configurata'}{c.smtp_enabled ? '' : ' (disattiva)'}</option>)}
        </select>
        <p style={s.help}>Le caselle si aggiungono in Impostazioni → Email / SMTP.</p>
        <label style={{ ...s.label, marginTop: '12px' }}>OGGETTO</label>
        <input style={s.inp} maxLength={200} value={f.email_oggetto || ''} onChange={e => set('email_oggetto', e.target.value)} placeholder={`${f.titolo} — conferma`} />
        <label style={{ ...s.label, marginTop: '12px' }}>TESTO DELL'EMAIL</label>
        <textarea style={{ ...s.inp, minHeight: '110px', resize: 'vertical' }} maxLength={5000} value={f.email_testo || ''} onChange={e => set('email_testo', e.target.value)} placeholder={f.messaggio_conferma || 'Grazie, abbiamo ricevuto le tue risposte.'} />
        <p style={s.help}>Vuoto = lo stesso messaggio che vedono a video dopo l'invio. Puoi scrivere qui istruzioni più lunghe (cosa portare, orari, contatti). {f.genera_codice ? 'Il codice viene aggiunto sotto in automatico. ' : ''}Le risposte di chi compila non vengono copiate nell'email.</p>
      </div>}
    </div>
  );
}

const CORREGGIBILI = ['TESTO_BREVE', 'TESTO_LUNGO', 'EMAIL', 'TELEFONO', 'NUMERO', 'DATA', 'SCELTA_SINGOLA', 'SCELTA_MULTIPLA', 'ELENCO', 'SI_NO', 'VALUTAZIONE', 'CODICE_FISCALE'];
function Risposte({ f, puoEliminare, puoModificare, puoAggiungere, onCambio }) {
  const [risposte, setRisposte] = useState(null);
  const [aperta, setAperta] = useState(null);
  const [anteprima, setAnteprima] = useState(null);
  const [docAperto, setDocAperto] = useState(null); // regolamento come era quando è stato accettato (copia nella risposta)
  const [cerca, setCerca] = useState('');
  // vista tabella (tutte insieme, stile Excel) o schede una per una; si ricorda su questo computer
  const [vista, setVistaStato] = useState(() => { try { return localStorage.getItem('form_risposte_vista') || 'tabella'; } catch { return 'tabella'; } });
  const setVista = (v) => { setVistaStato(v); try { localStorage.setItem('form_risposte_vista', v); } catch { /* niente */ } };
  const [inFinestra, setInFinestra] = useState(null); // risposta aperta dalla tabella
  const [correggi, setCorreggi] = useState(null); // { id, dati } risposta in correzione
  const [nuova, setNuova] = useState(null); // { dati, invia_email, err, salvando } risposta compilata a mano dallo staff
  const [pagamenti, setPagamenti] = useState(null); // strumento Pagamento (FormStrumenti.js), null = spento
  const [pagAperto, setPagAperto] = useState(null); // id della risposta col pagamento aperto
  const conPagamento = !!f.strumenti?.pagamento, conStatistiche = !!f.strumenti?.statistiche;
  const caricaPagamenti = () => conPagamento ? api.get(`/form/${f.id}/pagamenti`).then(r => setPagamenti(r.data)).catch(() => setPagamenti(null)) : Promise.resolve(setPagamenti(null));
  const carica = () => { caricaPagamenti(); return api.get(`/form/${f.id}/risposte`).then(r => setRisposte(r.data)).catch(() => setRisposte([])); };
  useEffect(() => { carica(); }, [f.id, conPagamento]); // eslint-disable-line
  useEffect(() => { if (vista === 'statistiche' && !conStatistiche) setVista('tabella'); }, [vista, conStatistiche]); // eslint-disable-line

  const filtrate = useMemo(() => {
    const t = cerca.trim().toLowerCase();
    if (!t || !risposte) return risposte;
    return risposte.filter(r => `${r.codice || ''} ${JSON.stringify(r.dati)}`.toLowerCase().includes(t));
  }, [risposte, cerca]);

  // Riassunto di una riga: le prime due risposte testuali
  const riassunto = (r) => (r.campi_snapshot || []).filter(c => !['SEZIONE', 'INFO', 'ALLEGATO', 'CONSENSO', 'FIRMA', 'DOCUMENTO'].includes(c.tipo)).slice(0, 3)
    .map(c => r.dati?.[c.id]).filter(v => v !== undefined && v !== '').map(v => Array.isArray(v) ? v.join(', ') : String(v)).join(' · ');

  const esporta = async () => {
    try {
      const r = await api.get(`/form/${f.id}/export`, { responseType: 'blob' });
      const nome = (r.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || 'Risposte.xlsx';
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = nome; a.click(); URL.revokeObjectURL(url);
    } catch (e) { alert('Errore export'); }
  };
  const scarica = async (a) => {
    try {
      const r = await api.get(`/form/${f.id}/allegati/${a.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const el = document.createElement('a'); el.href = url; el.download = a.nome_originale; el.click(); URL.revokeObjectURL(url);
    } catch { alert('Errore download'); }
  };
  const elimina = async (r) => {
    if (!window.confirm(`Eliminare questa risposta${r.codice ? ` (${r.codice})` : ''} e i suoi allegati? La scheda in anagrafica, se creata, resta.`)) return;
    try { await api.delete(`/form/${f.id}/risposte/${r.id}`); setInFinestra(null); carica(); onCambio(); } catch (e) { alert(e.response?.data?.error || 'Errore'); }
  };

  // Correzione dallo staff (25/10/2026): solo i campi di dati, firme/consensi/allegati restano come inviati.
  // Per le scelte valgono le opzioni attuali del form (es. una taglia aggiunta dopo).
  const campoAttuale = (c) => (f.campi || []).find(x => x.id === c.id && x.tipo === c.tipo) || c;
  const salvaCorrezione = async (r) => {
    const cambiati = {};
    for (const [k, v] of Object.entries(correggi.dati)) if (JSON.stringify(v ?? '') !== JSON.stringify(r.dati?.[k] ?? '')) cambiati[k] = v;
    if (!Object.keys(cambiati).length) { setCorreggi(null); return; }
    try {
      const { data } = await api.put(`/form/${f.id}/risposte/${r.id}`, { dati: cambiati });
      setRisposte(rs => rs.map(x => x.id === r.id ? { ...x, dati: data.dati, modificata_il: data.modificata_il, modificata_da: data.modificata_da } : x));
      if (inFinestra?.id === r.id) setInFinestra(x => ({ ...x, dati: data.dati, modificata_il: data.modificata_il, modificata_da: data.modificata_da }));
      setCorreggi(null);
    } catch (e) { alert(e.response?.data?.error || 'Non salvato'); }
  };
  const campoCorrezione = (c) => campoDato(campoAttuale(c), correggi.dati[c.id], (x) => setCorreggi(p => ({ ...p, dati: { ...p.dati, [c.id]: x } })));
  const campoDato = (c, v, set) => {
    const opz = c.opzioni || [];
    if (c.tipo === 'SCELTA_SINGOLA' || c.tipo === 'ELENCO') return (
      <select style={s.inp} value={v ?? ''} onChange={e => set(e.target.value)}>
        <option value="">— nessuna risposta —</option>
        {[...opz, ...(v && !opz.includes(v) ? [v] : [])].map(o => <option key={o} value={o}>{o}{opz.includes(o) ? '' : ' (non più tra le opzioni)'}</option>)}
      </select>);
    if (c.tipo === 'SCELTA_MULTIPLA') return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
        {opz.map(o => <label key={o} style={{ display: 'inline-flex', gap: '5px', alignItems: 'center', fontSize: 'var(--font-size-sm)' }}>
          <input type="checkbox" checked={(v || []).includes(o)} onChange={e => set(e.target.checked ? [...(v || []), o] : (v || []).filter(x => x !== o))} />{o}</label>)}
      </div>);
    if (c.tipo === 'SI_NO') return (
      <select style={s.inp} value={v ?? ''} onChange={e => set(e.target.value)}><option value="">—</option><option value="SI">Sì</option><option value="NO">No</option></select>);
    if (c.tipo === 'VALUTAZIONE') return (
      <select style={s.inp} value={v ?? ''} onChange={e => set(e.target.value ? +e.target.value : '')}><option value="">—</option>{[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}</select>);
    if (c.tipo === 'TESTO_LUNGO') return <textarea style={{ ...s.inp, minHeight: '70px' }} value={v ?? ''} onChange={e => set(e.target.value)} />;
    return <input style={s.inp} type={c.tipo === 'DATA' ? 'date' : c.tipo === 'NUMERO' ? 'number' : 'text'} value={v ?? ''} onChange={e => set(e.target.value)} />;
  };

  // Risposta compilata a mano dallo staff (26/10/2026): solo le domande di dati del form attuale
  const daCompilare = (f.campi || []).filter(c => CORREGGIBILI.includes(c.tipo));
  const salvaNuova = async () => {
    setNuova(n => ({ ...n, salvando: true, err: '' }));
    try {
      const { data } = await api.post(`/form/${f.id}/risposte`, { dati: nuova.dati, invia_email: nuova.invia_email });
      setNuova(null); await carica(); onCambio();
      setInFinestra({ ...data, allegati: [], persona: null });
    } catch (e) { setNuova(n => ({ ...n, salvando: false, err: e.response?.data?.error || 'Non salvata' })); }
  };

  // tutte le risposte di una persona (usato dalle schede e dalla finestra aperta dalla tabella)
  const dettaglio = (r, inTabella = false) => (
            <div style={{ ...(inTabella ? {} : { borderTop: '1px solid var(--border)' }), padding: inTabella ? '4px 0 0' : '12px 16px 14px 38px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {(r.campi_snapshot || []).filter(c => !SENZA_RISPOSTA.includes(c.tipo)).map(c => {
                const v = r.dati?.[c.id];
                const files = r.allegati.filter(a => a.campo_id === c.id);
                return (
                  <div key={c.id}>
                    <div style={s.label}>{c.etichetta}</div>
                    {c.tipo === 'DOCUMENTO' && <button style={{ ...s.btnSec, marginBottom: '4px' }} onClick={() => setDocAperto(c)}><Eye size={12} /> VEDI IL DOCUMENTO ACCETTATO</button>}
                    {correggi?.id === r.id && CORREGGIBILI.includes(c.tipo) ? campoCorrezione(c) : c.tipo === 'ALLEGATO' || (c.tipo === 'FIRMA' && files.length)
                      ? (files.length ? files.map(a => (
                          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: 'var(--font-size-sm)', marginTop: '4px' }}>
                            <Paperclip size={12} /><span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{a.nome_originale}</span>
                            <button style={s.btnSec} onClick={() => setAnteprima({ doc: { nome: a.nome_originale, file_nome: a.file_nome, tipo: 'Allegato', created_at: a.created_at }, endpoint: `/form/${f.id}/allegati/${a.id}` })}><Eye size={12} /> VEDI</button>
                            <button style={s.btnSec} onClick={() => scarica(a)}><Download size={12} /></button>
                          </div>)) : <div style={s.meta}>—</div>)
                      : <div style={{ fontSize: 'var(--font-size-sm)', whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>{v === undefined || v === '' ? <span style={s.meta}>—</span> : v === true && c.tipo === 'DOCUMENTO' ? 'Letto e accettato' : testoRisposta(c, v)}</div>}
                    {r.straniero?.campo_id === c.id && (
                      <div style={{ marginTop: '6px', padding: '8px 10px', borderRadius: '8px', background: 'var(--bg-secondary, #F4F4F5)', fontSize: 'var(--font-size-sm)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div><b>Straniero senza codice fiscale italiano</b>: codice calcolato dal gestionale · nato/a in {r.straniero.stato} · {r.straniero.documento_tipo === 'PASSAPORTO' ? 'Passaporto' : "Carta d'identità"} n. {r.straniero.documento_numero}</div>
                        {files.map(a => (
                          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Paperclip size={12} /><span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{a.nome_originale}</span>
                            <button style={s.btnSec} onClick={() => setAnteprima({ doc: { nome: a.nome_originale, file_nome: a.file_nome, tipo: 'Documento', created_at: a.created_at }, endpoint: `/form/${f.id}/allegati/${a.id}` })}><Eye size={12} /> VEDI</button>
                          </div>))}
                        <div style={s.meta}>Il documento si conferma da Anagrafica → VERIFICA DOCUMENTO.</div>
                      </div>
                    )}
                  </div>
                );
              })}
              {r.persona && <div style={s.meta}>Scheda anagrafica: {r.persona.cognome} {r.persona.nome} ({r.persona.codice_fiscale})</div>}
              {r.inserita_da && <div style={s.meta}>Inserita a mano da {r.inserita_da} il {new Date(r.created_at).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })} (non dal link del form)</div>}
              {r.modificata_il && <div style={s.meta}>Corretta da {r.modificata_da || 'staff'} il {new Date(r.modificata_il).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}</div>}
              {correggi?.id === r.id ? (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <button style={s.btnPri} onClick={() => salvaCorrezione(r)}><Check size={12} /> SALVA CORREZIONI</button>
                  <button style={s.btnSec} onClick={() => setCorreggi(null)}>ANNULLA</button>
                  <span style={s.meta}>Firme, consensi, regolamenti e allegati non si modificano. La scheda in anagrafica non cambia.</span>
                </div>
              ) : <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {puoModificare && <button style={{ ...s.btnSec, alignSelf: 'flex-start' }} onClick={() => setCorreggi({ id: r.id, dati: { ...(r.dati || {}) } })}><Pencil size={12} /> MODIFICA RISPOSTE</button>}
                {puoEliminare && <button style={{ ...s.btnSec, alignSelf: 'flex-start', color: '#EF4444' }} onClick={() => elimina(r)}><Trash2 size={12} /> ELIMINA RISPOSTA</button>}
              </div>}
            </div>
  );

  if (!risposte) return <div style={s.meta}>Caricamento…</div>;
  return <>
    <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
      <input style={{ ...s.inp, flex: 1, minWidth: '200px' }} placeholder="Cerca in tutte le risposte…" value={cerca} onChange={e => setCerca(e.target.value)} />
      {risposte.length > 0 && <div style={{ display: 'flex', border: '1px solid var(--border2)', borderRadius: '999px', overflow: 'hidden' }}>
        {[['tabella', 'TABELLA'], ['schede', 'UNA PER UNA'], ...(conStatistiche ? [['statistiche', 'STATISTICHE']] : [])].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setVista(k)} style={{ border: 0, padding: '7px 14px', fontSize: 'var(--font-size-xs)', fontWeight: 700, cursor: 'pointer', background: vista === k ? '#F4600C' : 'transparent', color: vista === k ? '#fff' : 'var(--text)' }}>{l}</button>
        ))}
      </div>}
      <button style={s.btnSec} onClick={esporta} disabled={!risposte.length}><Download size={13} /> EXCEL</button>
      {puoAggiungere && daCompilare.length > 0 && <button style={s.btnPri} onClick={() => setNuova({ dati: {}, invia_email: false, err: '', salvando: false })}><Plus size={13} /> AGGIUNGI A MANO</button>}
    </div>
    {nuova && (
      <InFinestra attiva onChiudi={() => setNuova(null)} larghezza={640}>
        <div style={{ padding: '18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ ...s.cardTitle, flex: 1, margin: 0 }}>NUOVA RISPOSTA A MANO</div>
            <button style={s.btnSec} onClick={() => setNuova(null)}><X size={13} /> CHIUDI</button>
          </div>
          <p style={{ ...s.help, margin: 0 }}>La compili tu al posto della persona, senza passare dal link del form (vale anche se il form è chiuso o pieno). Si chiedono solo le domande di dati: firme, consensi, regolamenti e allegati restano vuoti. Valgono gli stessi controlli del form{f.collega_anagrafica ? ', e con il codice fiscale si collega o si crea la scheda in anagrafica' : ''}. Nella risposta resta scritto chi l'ha inserita.</p>
          {nuova.err && <div style={{ color: '#EF4444', fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>{nuova.err}</div>}
          {daCompilare.map(c => (
            <div key={c.id}>
              <div style={s.label}>{c.etichetta}{c.obbligatorio ? ' *' : ''}</div>
              {campoDato(c, nuova.dati[c.id], (x) => setNuova(n => ({ ...n, dati: { ...n.dati, [c.id]: x } })))}
            </div>
          ))}
          {f.email_conferma && <label style={{ display: 'flex', gap: '7px', alignItems: 'center', fontSize: 'var(--font-size-sm)', cursor: 'pointer' }}>
            <input type="checkbox" checked={nuova.invia_email} onChange={e => setNuova(n => ({ ...n, invia_email: e.target.checked }))} style={{ accentColor: '#F4600C' }} />
            Manda anche l'email di conferma del form alla persona</label>}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button style={s.btnSec} onClick={() => setNuova(null)}>ANNULLA</button>
            <button style={s.btnPri} disabled={nuova.salvando} onClick={salvaNuova}><Check size={12} /> {nuova.salvando ? 'SALVO…' : 'SALVA RISPOSTA'}</button>
          </div>
        </div>
      </InFinestra>
    )}
    {!risposte.length && <div style={s.vuoto}>Nessuna risposta ancora. Le prove fatte in Anteprima non si salvano: qui arrivano gli invii dal link pubblico e quelle che aggiungi a mano.</div>}
    {risposte.length > 0 && vista !== 'statistiche' && <RiepilogoPagamenti dati={pagamenti} />}
    {risposte.length > 0 && vista === 'tabella' && <RisposteTabella f={f} risposte={risposte} cerca={cerca} onApri={setInFinestra} pagamenti={pagamenti} onPagamento={r => setPagAperto(r.id)} />}
    {risposte.length > 0 && vista === 'statistiche' && <Statistiche f={f} risposte={risposte} pagamenti={pagamenti} />}
    {pagAperto && pagamenti?.righe?.[pagAperto] && <FinestraPagamento f={f} riga={pagamenti.righe[pagAperto]} banca={pagamenti.banca} puoModificare={puoModificare}
      onChiudi={() => setPagAperto(null)} onCambio={caricaPagamenti} />}
    {inFinestra && (
      <InFinestra attiva onChiudi={() => setInFinestra(null)} larghezza={720}>
        <div style={{ padding: '18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            {inFinestra.codice && <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#F4600C' }}>{inFinestra.codice}</span>}
            <span style={{ ...s.meta, flex: 1 }}>{new Date(inFinestra.created_at).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}</span>
            <button style={s.btnSec} onClick={() => setInFinestra(null)}><X size={13} /> CHIUDI</button>
          </div>
          {dettaglio(inFinestra, true)}
        </div>
      </InFinestra>
    )}
    {vista === 'schede' && <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {filtrate.map(r => (
        <div key={r.id} style={{ ...s.card, padding: 0, marginBottom: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', cursor: 'pointer' }} onClick={() => setAperta(aperta === r.id ? null : r.id)}>
            {aperta === r.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            {r.codice && <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#F4600C' }}>{r.codice}</span>}
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 'var(--font-size-sm)' }}>{riassunto(r)}</span>
            {r.allegati.length > 0 && <span style={s.meta}><Paperclip size={11} /> {r.allegati.length}</span>}
            {pagamenti?.righe?.[r.id] && <span onClick={e => { e.stopPropagation(); setPagAperto(r.id); }} title="Apri il pagamento"><BadgePagamento p={pagamenti.righe[r.id]} /></span>}
            {r.inserita_da && <span style={{ ...s.badge, color: '#8A3B00', borderColor: 'var(--border2)' }} title={`Inserita a mano da ${r.inserita_da}`}>A MANO</span>}
            {r.esito_anagrafica && <span style={{ ...s.badge, color: r.esito_anagrafica === 'CREATA' ? '#22C55E' : '#3B82F6', borderColor: 'var(--border2)' }}>{r.esito_anagrafica === 'CREATA' ? 'NUOVA SCHEDA' : 'GIÀ IN ANAGRAFICA'}</span>}
            <span style={s.meta}>{new Date(r.created_at).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}</span>
          </div>
          {aperta === r.id && dettaglio(r)}
        </div>
      ))}
    </div>}
    {anteprima && <AnteprimaDocumento doc={anteprima.doc} endpoint={anteprima.endpoint} onClose={() => setAnteprima(null)} />}
    {docAperto && <DocumentoAccettato formId={f.id} c={docAperto} onChiudi={() => setDocAperto(null)} />}
  </>;
}

// "i" accanto a Dato della persona: spiegazione al passaggio del mouse (o al tocco da telefono)
function NotaDatoPersona({ auto }) {
  const [aperta, setAperta] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-flex' }} onMouseEnter={() => setAperta(true)} onMouseLeave={() => setAperta(false)}>
      <button type="button" aria-label="Cos'è Dato della persona" onClick={e => { e.preventDefault(); setAperta(a => !a); }} onBlur={() => setAperta(false)}
        style={{ background: 'none', border: 0, padding: 2, cursor: 'help', color: 'var(--text-muted)', display: 'inline-flex' }}><Info size={15} /></button>
      {aperta && (
        <span role="tooltip" style={{ position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)', width: 300, maxWidth: '80vw', zIndex: 50,
          background: '#1D1D1F', color: '#fff', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, lineHeight: 1.45, fontWeight: 400, boxShadow: '0 8px 24px rgba(0,0,0,.25)', textAlign: 'left' }}>
          {auto
            ? <><b>Già a posto:</b> il gestionale ha capito dal titolo che questa domanda è il {auto.toLowerCase()} della persona. Non serve scegliere niente.</>
            : <>Di solito non serve: le domande intitolate Nome, Cognome, Data di nascita, Luogo di nascita e quelle di tipo Codice fiscale vengono <b>riconosciute da sole</b> (badge blu RICONOSCIUTA).</>}
          <span style={{ display: 'block', marginTop: 6 }}>Sceglilo a mano solo se il titolo è diverso (es. "Come ti chiami?") o se ci sono due domande uguali (atleta e compagno). Serve al controllo del codice fiscale e, col flag in Impostazioni, a creare la scheda in anagrafica.</span>
        </span>
      )}
    </span>
  );
}

// Indirizzo del form: /f/<link>. Si può scegliere un nome leggibile; se il form è già pubblicato
// il vecchio indirizzo continua ad aprirlo (QR stampati, messaggi già mandati).
function LinkForm({ f, set, slugSalvato }) {
  const base = `${window.location.host}/f/`;
  const cambiato = f.slug !== slugSalvato;
  const corto = (f.slug || '').replace(/-+$/, '').length < 3;
  const vecchi = (f.vecchi_slug || []).filter(x => x !== f.slug);
  return (
    <div style={s.card}>
      <div style={s.cardTitle}>LINK DEL FORM</div>
      <Spiega>è l'indirizzo da mandare alle persone (WhatsApp, email, QR code). Puoi dargli un nome leggibile al posto delle lettere a caso, per esempio /f/club-series. Si attiva con SALVA.</Spiega>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0', flexWrap: 'wrap', rowGap: '6px' }}>
        <span style={{ ...s.meta, padding: '9px 10px', background: 'var(--bg-input)', borderRadius: '12px 0 0 12px', border: '1px solid var(--border2)', borderRight: 0, whiteSpace: 'nowrap' }}>{base}</span>
        <input style={{ ...s.inp, flex: '1 1 220px', width: 'auto', borderRadius: '0 12px 12px 0', fontWeight: 600 }} maxLength={60} value={f.slug || ''}
          onChange={e => set('slug', comeSlug(e.target.value))} placeholder="open-day-2026" aria-label="Ultima parte del link" />
      </div>
      <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
        <button type="button" style={s.btnSec} onClick={() => set('slug', comeSlug(f.titolo).replace(/-+$/, ''))}>PROPONI DAL TITOLO</button>
        {cambiato && <button type="button" style={s.btnSec} onClick={() => set('slug', slugSalvato)}>ANNULLA</button>}
      </div>
      {corto && <p style={{ ...s.help, color: '#F59E0B' }}>Almeno 3 caratteri: lettere, numeri e trattini.</p>}
      <p style={s.help}>Solo lettere minuscole, numeri e trattini (spazi e accenti si sistemano da soli). Esempio: <b>{base}open-day-2026</b>.
        {f.stato === 'BOZZA' ? ' Il link funziona dopo PUBBLICA.' : cambiato ? <> Dopo il SALVA il form risponde al nuovo indirizzo; <b>quello vecchio continua ad aprirlo</b> (QR e messaggi già mandati restano validi).</> : ''}</p>
      {vecchi.length > 0 && <p style={s.help}>Aprono ancora questo form anche: {vecchi.map(x => <code key={x} style={{ marginRight: 8 }}>/f/{x}</code>)}</p>}
    </div>
  );
}

// Firma: come si firma, scelto da chi prepara il form
function ImpostazioniFirma({ c, i, setCampo, conNome }) {
  const modo = c.modo_firma || 'dito';
  return (
    <div style={{ marginTop: '10px' }}>
      <label style={s.label}>COME SI FIRMA</label>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        {MODI_FIRMA.map(m => (
          <button key={m.modo} type="button" onClick={() => setCampo(i, { modo_firma: m.modo, ...(m.modo !== 'dito' && c.firma_coincide === undefined ? { firma_coincide: true } : {}) })}
            style={{ ...s.btnSec, ...(modo === m.modo ? { background: '#F4600C', color: '#fff', borderColor: '#F4600C' } : {}) }}>{m.label}</button>
        ))}
      </div>
      <div style={{ ...s.help, marginTop: '6px' }}>{MODI_FIRMA.find(m => m.modo === modo)?.aiuto} Nelle risposte trovi la firma (immagine) oppure il nome scritto, con data e ora dell'invio.</div>
      {modo !== 'dito' && (
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '7px', fontSize: 'var(--font-size-sm)', cursor: 'pointer', marginTop: '8px' }}>
          <input type="checkbox" checked={c.firma_coincide !== false} onChange={e => setCampo(i, { firma_coincide: e.target.checked })} style={{ accentColor: '#F4600C', marginTop: '3px' }} />
          <span>Il nome scritto deve coincidere con Nome e Cognome del modulo
            <span style={{ display: 'block', ...s.help }}>{conNome ? 'Togli la spunta se firma qualcun altro (es. il genitore di un minore).' : 'Nel modulo non ci sono domande Nome e Cognome riconosciute: il controllo non si applica.'}</span></span>
        </label>
      )}
    </div>
  );
}

// Regolamento da sottoscrivere: testo impaginato (segni semplici) e/o pagine immagine, poi la spunta.
const BOTTONI_TESTO = [
  { l: 'Titolo', riga: '# ', esempio: 'Titolo del regolamento' },
  { l: 'Articolo / sottotitolo', riga: '## ', esempio: 'Art. 1 – Oggetto' },
  { l: 'Grassetto', dentro: '**', esempio: 'testo in grassetto' },
  { l: 'Elenco puntato', riga: '- ', esempio: 'voce' },
  { l: 'Elenco numerato', riga: '1. ', esempio: 'voce' },
  { l: 'Nuova pagina', blocco: '\n---\n' },
];
// info = "Testo da leggere": stesso testo e pagine, senza la spunta di accettazione
function EditorDocumento({ c, i, setCampo, fotoMap, caricaPagina, info = false }) {
  const area = useRef(null);
  const [vedi, setVedi] = useState(false);
  const [caricando, setCaricando] = useState(false);
  const pagine = c.pagine_doc || [];
  const testo = c.testo_doc || '';
  const scrivi = (v) => setCampo(i, { testo_doc: v });
  // i bottoni lavorano sul testo selezionato (o inseriscono un esempio)
  const applica = (b) => {
    const el = area.current;
    const da = el ? el.selectionStart : testo.length, a = el ? el.selectionEnd : testo.length;
    const sel = testo.slice(da, a);
    let nuovo, cursore;
    if (b.blocco) { nuovo = testo.slice(0, da) + b.blocco + testo.slice(a); cursore = da + b.blocco.length; }
    else if (b.dentro) { const t = sel || b.esempio; nuovo = testo.slice(0, da) + b.dentro + t + b.dentro + testo.slice(a); cursore = da + b.dentro.length + t.length + b.dentro.length; }
    else {
      // righe intere: quella del cursore, oppure tutte quelle toccate dalla selezione
      const inizio = testo.lastIndexOf('\n', da - 1) + 1;
      let fine = testo.indexOf('\n', sel && testo[a - 1] === '\n' ? a - 1 : a);
      if (fine < 0) fine = testo.length;
      const righe = (testo.slice(inizio, fine) || b.esempio).split('\n')
        .map((r, k) => (b.riga === '1. ' ? `${k + 1}. ` : b.riga) + r.replace(/^(#{1,2}\s+|[-•]\s+|\d+[.)]\s+)/, ''));
      const blocco = righe.join('\n');
      nuovo = testo.slice(0, inizio) + blocco + testo.slice(fine);
      cursore = inizio + blocco.length;
    }
    scrivi(nuovo.slice(0, 30000));
    setTimeout(() => { if (el) { el.focus(); el.setSelectionRange(cursore, cursore); } }, 0);
  };
  const aggiungiPagine = () => {
    const inp = document.createElement('input');
    inp.type = 'file'; inp.accept = 'image/jpeg,image/png,image/webp'; inp.multiple = true;
    inp.onchange = async () => {
      const files = [...(inp.files || [])].slice(0, 20 - pagine.length);
      if (!files.length) return;
      setCaricando(true);
      const nomi = [];
      try { for (const file of files) nomi.push(await caricaPagina(file)); }
      catch (e) { alert(e.response?.data?.error || e.message || 'Pagina non caricata'); }
      finally {
        setCaricando(false);
        if (nomi.length) setCampo(i, { pagine_doc: [...pagine, ...nomi] });
      }
    };
    inp.click();
  };
  // PDF: ogni pagina diventa un'immagine (così si legge da telefono e la copia nella risposta è quella vista)
  const caricaPdf = () => {
    const inp = document.createElement('input');
    inp.type = 'file'; inp.accept = 'application/pdf,.pdf';
    inp.onchange = async () => {
      const file = inp.files?.[0];
      if (!file) return;
      const posti = 20 - pagine.length;
      setCaricando('Apro il PDF…');
      const nomi = [];
      let doc;
      try {
        doc = await apriPdf(file);
        let quante = doc.numPages;
        if (quante > posti) {
          if (!window.confirm(`Il PDF ha ${quante} pagine, ma ne entrano ancora ${posti} (massimo 20). Carico le prime ${posti}?`)) return;
          quante = posti;
        }
        for (let k = 1; k <= quante; k++) {
          setCaricando(`Pagina ${k} di ${quante}…`);
          nomi.push(await caricaPagina(await paginaPdf(doc, k)));
        }
      } catch (e) { alert(e.response?.data?.error || e.message || 'PDF non caricato'); }
      finally {
        chiudiPdf(doc);
        setCaricando(false);
        if (nomi.length) setCampo(i, { pagine_doc: [...pagine, ...nomi] });
      }
    };
    inp.click();
  };
  const spostaPagina = (k, d) => { const p = [...pagine]; const j = k + d; if (j < 0 || j >= p.length) return; [p[k], p[j]] = [p[j], p[k]]; setCampo(i, { pagine_doc: p }); };
  return (
    <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div>
        <label style={s.label}>{info ? 'TESTO DA FAR LEGGERE' : 'TESTO DEL REGOLAMENTO'}</label>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
          {BOTTONI_TESTO.map(b => <button key={b.l} type="button" style={{ ...s.btnSec, padding: '4px 10px', fontSize: 'var(--font-size-xs)' }} onClick={() => applica(b)}>{b.l}</button>)}
          <button type="button" style={{ ...s.btnSec, padding: '4px 10px', fontSize: 'var(--font-size-xs)', marginLeft: 'auto' }} onClick={() => setVedi(v => !v)}><Eye size={12} /> {vedi ? 'NASCONDI FOGLIO' : 'VEDI COME FOGLIO'}</button>
        </div>
        <textarea ref={area} style={{ ...s.inp, minHeight: '220px', resize: 'vertical', fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: '13px', lineHeight: 1.55 }} maxLength={30000}
          value={testo} onChange={e => scrivi(e.target.value)} placeholder={info ? '## Come funziona\nGli allenamenti iniziano a…\n\n- porta l\'acqua\n- arriva 10 minuti prima' : '# Regolamento B-Cheers 2026/2027\n\n## Art. 1 – Iscrizione\nL\'iscrizione si perfeziona con…\n\n- prima regola\n- seconda regola'} />
        <div style={s.help}>Riga che inizia con <b>#</b> = titolo, <b>##</b> = articolo, <b>-</b> = elenco, <b>1.</b> = elenco numerato, <b>**parola**</b> = grassetto, <b>---</b> = nuova pagina. Riga vuota = nuovo paragrafo. Puoi anche incollare il testo da Word e poi sistemare i titoli.</div>
        {vedi && <div className="fv-foglio" style={{ marginTop: '10px', maxHeight: '420px' }}>{testo ? <TestoImpaginato testo={testo} /> : <p style={{ color: '#999' }}>Il foglio è vuoto.</p>}</div>}
      </div>
      <div>
        <label style={s.label}>PAGINE DA PDF O IMMAGINI (FACOLTATIVE, FINO A 20)</label>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {pagine.map((n, k) => (
            <div key={n} style={{ width: 96, textAlign: 'center' }}>
              <div style={{ position: 'relative' }}>
                <img src={fotoMap[n] || ''} alt={`Pagina ${k + 1}`} style={{ width: 96, height: 128, objectFit: 'cover', objectPosition: 'top', borderRadius: 6, border: '1px solid var(--border2)', background: '#fff', display: 'block' }} />
                <button type="button" title="Togli pagina" onClick={() => setCampo(i, { pagine_doc: pagine.filter(x => x !== n) })}
                  style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', border: 0, background: '#1D1D1F', color: '#fff', display: 'grid', placeItems: 'center', padding: 0, cursor: 'pointer' }}><X size={12} /></button>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '2px', marginTop: '3px' }}>
                <button type="button" style={s.iconBtn} disabled={k === 0} onClick={() => spostaPagina(k, -1)} title="Prima"><ArrowLeft size={13} /></button>
                <span style={s.meta}>{k + 1}</span>
                <button type="button" style={s.iconBtn} disabled={k === pagine.length - 1} onClick={() => spostaPagina(k, 1)} title="Dopo"><ArrowRight size={13} /></button>
              </div>
            </div>
          ))}
          {pagine.length < 20 && (caricando ? (
            <div style={{ ...s.iconBtn, width: 96, height: 128, border: '1px dashed var(--border2)', borderRadius: 6, fontSize: '11px', textAlign: 'center', padding: '6px' }}>{caricando === true ? 'Carico…' : caricando}</div>
          ) : <>
            <button type="button" onClick={caricaPdf}
              style={{ ...s.iconBtn, width: 96, height: 128, border: '1px dashed var(--border2)', borderRadius: 6, flexDirection: 'column', gap: '4px', fontSize: '11px' }}>
              <Paperclip size={18} /> CARICA PDF
            </button>
            <button type="button" onClick={aggiungiPagine}
              style={{ ...s.iconBtn, width: 96, height: 128, border: '1px dashed var(--border2)', borderRadius: 6, flexDirection: 'column', gap: '4px', fontSize: '11px' }}>
              <ImagePlus size={18} /> AGGIUNGI IMMAGINI
            </button>
          </>)}
        </div>
        <div style={{ ...s.help, marginTop: '6px' }}>Un PDF (ogni pagina diventa un'immagine) oppure foto e scansioni (JPG, PNG). Compaiono sotto il testo, una dopo l'altra, e si possono riordinare. Hai il {info ? 'testo' : 'regolamento'} in Word? Salvalo in PDF, oppure copia il testo qui sopra (da telefono si legge ancora meglio).</div>
      </div>
      {info ? <div style={s.help}>Chi compila lo legge e va avanti: non deve rispondere né spuntare niente. Se serve che lo accetti, usa <b>Regolamento da sottoscrivere</b> (si cambia dal menu del tipo, il testo resta).</div> : <>
      <div>
        <label style={s.label}>FRASE DA SPUNTARE SOTTO IL FOGLIO</label>
        <input style={s.inp} maxLength={300} value={c.testo_accetta ?? ACCETTA_DEFAULT} onChange={e => setCampo(i, { testo_accetta: e.target.value })} placeholder={ACCETTA_DEFAULT} />
      </div>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '7px', fontSize: 'var(--font-size-sm)', cursor: 'pointer' }}>
        <input type="checkbox" checked={c.leggi_tutto !== false} onChange={e => setCampo(i, { leggi_tutto: e.target.checked })} style={{ accentColor: '#F4600C', marginTop: '3px' }} />
        <span>Si può accettare solo dopo aver fatto scorrere il foglio fino in fondo</span>
      </label>
      <div style={s.help}>Ogni risposta conserva il regolamento come era al momento dell'invio: se lo cambi, chi aveva già accettato resta legato alla versione che ha letto (in Risposte: "Vedi il documento accettato"). Per far firmare, aggiungi subito dopo una domanda <b>Firma</b>.</div>
      </>}
    </div>
  );
}

// Regolamento come era quando la persona l'ha accettato (dalla copia salvata nella risposta)
function DocumentoAccettato({ formId, c, onChiudi }) {
  const [url, setUrl] = useState({});
  const chiudi = useCallback(() => onChiudi(), [onChiudi]);
  useEffect(() => {
    let vivi = true; const creati = [];
    Promise.all((c.pagine_doc || []).map(n => api.get(`/form/${formId}/foto-opzione/${n}`, { responseType: 'blob' })
      .then(r => { const u = URL.createObjectURL(r.data); creati.push(u); return [n, u]; }).catch(() => [n, null])))
      .then(coppie => { if (vivi) setUrl(Object.fromEntries(coppie)); });
    return () => { vivi = false; creati.forEach(u => URL.revokeObjectURL(u)); };
  }, [formId, c]);
  return (
    <InFinestra attiva onChiudi={chiudi} larghezza={860}>
      <div style={{ padding: '18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
          <div style={{ ...s.cardTitle, margin: 0, flex: 1 }}>{c.etichetta}</div>
          <button style={s.btnSec} onClick={chiudi}><X size={13} /> CHIUDI</button>
        </div>
        <div className="fv-foglio" style={{ maxHeight: '70vh' }}>
          {c.testo_doc && <TestoImpaginato testo={c.testo_doc} />}
          {(c.pagine_doc || []).map((n, k) => url[n] ? <img key={n} className="fv-foglio-pag" src={url[n]} alt={`Pagina ${k + 1}`} /> : <p key={n} style={{ color: '#999' }}>Pagina {k + 1} non più disponibile</p>)}
        </div>
        <div style={{ ...s.meta, marginTop: '8px' }}>Spunta accettata: "{c.testo_accetta || ACCETTA_DEFAULT}"</div>
      </div>
    </InFinestra>
  );
}

const barraSalva = { position: 'sticky', bottom: 0, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', background: 'var(--bg)', borderTop: '1px solid var(--border)', padding: '12px 0', marginTop: '8px' };
